import { cn } from '@/lib/utils';
import { useAppDispatch } from '@/store';
import {
  setAgentApplyFilters,
  type AgentFilterCondition,
} from '@/store/features/ui.feature';
import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';

/** A text link in a step or a card, as "Open run" under a step. */
export const LIST_ROWS_LINK =
  'shrink-0 font-medium text-foreground/80 underline decoration-border underline-offset-[3px] hover:text-foreground';

/**
 * Opens a list, showing the rows a step or a run reached: with `filters`,
 * the list's filter is set to them through Clu's own apply-filters command
 * (an empty set clears it); without, the list opens as the user left it.
 * Already on that list with nothing to apply, the link would do nothing
 * (replay 2026-10-23: clicked again and again), so it says so instead.
 */
export function ListRowsLink({
  listId,
  filters,
  className,
  children,
}: {
  listId: string;
  filters?: AgentFilterCondition[];
  className?: string;
  children: ReactNode;
}) {
  const dispatch = useAppDispatch();
  const onThisList = useRouterState({
    select: (state) => state.location.pathname === `/lists/${listId}`,
  });
  if (onThisList && !filters) {
    return (
      // The caller's link look (size kept), minus what makes it read as a
      // link: this is a plain fact, nothing to click.
      <span
        className={cn(
          'shrink-0 text-muted-foreground',
          className,
          'cursor-default text-muted-foreground no-underline hover:text-muted-foreground'
        )}
      >
        On this list
      </span>
    );
  }
  return (
    <Link
      to='/lists/$listId'
      params={{ listId }}
      data-list-rows-link
      onClick={() => {
        if (filters) dispatch(setAgentApplyFilters({ listId, filters }));
      }}
      className={cn(LIST_ROWS_LINK, className)}
    >
      {children}
    </Link>
  );
}
